import React, { useState, useEffect, useCallback } from 'react';

const API_BASE = '/api';
const BRAND = '#FF385C';

const headers = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

const RoleBadge = ({ role }) => (
  <span style={{
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 20,
    fontSize: '0.75rem',
    fontWeight: 600,
    background: role === 'admin' ? '#fef2f2' : '#f0fdf4',
    color: role === 'admin' ? BRAND : '#16a34a',
    border: `1px solid ${role === 'admin' ? '#fecaca' : '#bbf7d0'}`,
  }}>
    {role}
  </span>
);

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 0 });
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [roleModal, setRoleModal] = useState(null); // { user, newRole }

  const fetchUsers = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page, limit: 20, search });
      const res = await fetch(`${API_BASE}/admin/users?${params}`, { headers: headers() });
      if (!res.ok) throw new Error('Failed to load users');
      const data = await res.json();
      setUsers(data.users);
      setPagination(data.pagination);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const debounce = setTimeout(() => fetchUsers(1), 300);
    return () => clearTimeout(debounce);
  }, [fetchUsers]);

  const handleRoleChange = async () => {
    if (!roleModal) return;
    try {
      const res = await fetch(`${API_BASE}/admin/users/${roleModal.user.id}/role`, {
        method: 'PATCH',
        headers: headers(),
        body: JSON.stringify({ role: roleModal.newRole }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Role update failed');
      }
      setRoleModal(null);
      setSuccess(`Role updated to ${roleModal.newRole}`);
      setTimeout(() => setSuccess(null), 3000);
      fetchUsers(pagination.page);
    } catch (err) {
      setError(err.message);
      setRoleModal(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_BASE}/admin/users/${deleteModal.id}`, {
        method: 'DELETE',
        headers: headers(),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Delete failed');
      }
      setDeleteModal(null);
      setSuccess('User deleted');
      setTimeout(() => setSuccess(null), 3000);
      fetchUsers(pagination.page);
    } catch (err) {
      setError(err.message);
      setDeleteModal(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', marginBottom: '1.5rem' }}>Users</h2>

      {/* Search */}
      <div style={{ marginBottom: '1rem' }}>
        <input
          type="text"
          placeholder="Search by email or name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: '100%', maxWidth: 400, padding: '0.6rem 1rem', border: '1px solid #d1d5db',
            borderRadius: 8, fontSize: '0.9rem', outline: 'none',
          }}
        />
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '0.75rem 1rem', color: '#991b1b', marginBottom: '1rem' }}>{error}</div>
      )}
      {success && (
        <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 10, padding: '0.75rem 1rem', color: '#065f46', marginBottom: '1rem' }}>{success}</div>
      )}

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div className="spinner-border" role="status" style={{ color: BRAND }}>
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : users.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>No users found</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
                  {['Email', 'Name', 'Role', 'Stores', 'Joined', 'Actions'].map((h) => (
                    <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '0.75rem 1rem', color: '#111827', fontWeight: 500 }}>{user.email}</td>
                    <td style={{ padding: '0.75rem 1rem', color: '#4b5563' }}>{user.name || '—'}</td>
                    <td style={{ padding: '0.75rem 1rem' }}><RoleBadge role={user.role} /></td>
                    <td style={{ padding: '0.75rem 1rem', color: '#4b5563' }}>{user.store_count}</td>
                    <td style={{ padding: '0.75rem 1rem', color: '#6b7280', whiteSpace: 'nowrap' }}>
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap' }}>
                      {user.role === 'owner' ? (
                        <button
                          onClick={() => setRoleModal({ user, newRole: 'admin' })}
                          style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500, marginRight: 12 }}
                        >
                          Promote
                        </button>
                      ) : (
                        <button
                          onClick={() => setRoleModal({ user, newRole: 'owner' })}
                          style={{ background: 'none', border: 'none', color: '#f59e0b', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500, marginRight: 12 }}
                        >
                          Demote
                        </button>
                      )}
                      <button
                        onClick={() => setDeleteModal(user)}
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500 }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: '1.25rem' }}>
          {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              onClick={() => fetchUsers(p)}
              style={{
                width: 36, height: 36, borderRadius: 8, border: '1px solid #d1d5db',
                background: p === pagination.page ? BRAND : '#fff',
                color: p === pagination.page ? '#fff' : '#374151',
                fontWeight: 500, cursor: 'pointer', fontSize: '0.85rem',
              }}
            >
              {p}
            </button>
          ))}
        </div>
      )}

      {/* Role change modal */}
      {roleModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }}>
          <div style={{
            background: '#fff', borderRadius: 14, padding: '2rem', maxWidth: 420, width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#111827', marginBottom: '0.75rem' }}>
              {roleModal.newRole === 'admin' ? 'Promote to Admin?' : 'Demote to Owner?'}
            </h3>
            <p style={{ color: '#4b5563', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
              Change <strong>{roleModal.user.email}</strong> role to <strong>{roleModal.newRole}</strong>?
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setRoleModal(null)} style={{ padding: '0.6rem 1.25rem', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer', fontWeight: 500 }}>Cancel</button>
              <button onClick={handleRoleChange} style={{ padding: '0.6rem 1.25rem', borderRadius: 8, border: 'none', background: BRAND, color: '#fff', cursor: 'pointer', fontWeight: 500 }}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete modal */}
      {deleteModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }}>
          <div style={{
            background: '#fff', borderRadius: 14, padding: '2rem', maxWidth: 420, width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#111827', marginBottom: '0.75rem' }}>Delete User?</h3>
            <p style={{ color: '#4b5563', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
              This will soft-delete <strong>{deleteModal.email}</strong>. Their stores will remain but they won't be able to log in.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteModal(null)} disabled={deleting} style={{ padding: '0.6rem 1.25rem', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer', fontWeight: 500 }}>Cancel</button>
              <button onClick={handleDelete} disabled={deleting} style={{ padding: '0.6rem 1.25rem', borderRadius: 8, border: 'none', background: '#ef4444', color: '#fff', cursor: 'pointer', fontWeight: 500, opacity: deleting ? 0.6 : 1 }}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
